import type { AppManifest } from "@tchoma/app";
import { parseWebUrl } from "./model.ts";

export const CAPTURE_ROUTE_SEGMENT = "capture";
export const CAPTURE_FRAGMENT_MARKER = "tchoma-inbox-v1=";
export const CAPTURE_INPUT_MAX_BYTES = 2_048;

export type CaptureHandoffPlan =
  | { status: "ready"; routePrefix: string; publicOrigin: string }
  | { status: "gated"; reason: "missing-public-url" | "missing-network-permission" };

export type CaptureHandoffInput =
  | { status: "absent" }
  | { status: "invalid"; reason: "route" | "encoding" | "empty" | "too-large" | "unsafe-text" }
  | { status: "replay" }
  | { status: "ready"; kind: "text" | "url"; text: string; byteLength: number };

/**
 * Plans the only currently safe Shortcut → PWA handoff: a stable HTTPS route
 * whose fragment stays client-side. The origin still needs the public network
 * capability because opening a deployed URL is a physical network effect.
 */
export function planCaptureHandoff(app: AppManifest): CaptureHandoffPlan {
  const publicUrl = app.surfaces.web.publicUrl;
  if (!publicUrl) return { status: "gated", reason: "missing-public-url" };
  const parsed = new URL(publicUrl);
  if (!app.capabilities.includes("network") || !app.network.allowedOrigins.includes(parsed.origin)) {
    return { status: "gated", reason: "missing-network-permission" };
  }
  const base = new URL(parsed.href);
  if (!base.pathname.endsWith("/")) base.pathname += "/";
  const captureUrl = new URL(CAPTURE_ROUTE_SEGMENT, base);
  captureUrl.hash = CAPTURE_FRAGMENT_MARKER;
  return { status: "ready", routePrefix: captureUrl.href, publicOrigin: parsed.origin };
}

export function parseCaptureHandoff(urlInput: string | URL): CaptureHandoffInput {
  let url: URL;
  try {
    url = typeof urlInput === "string" ? new URL(urlInput) : new URL(urlInput.href);
  } catch {
    return { status: "invalid", reason: "route" };
  }
  const localDevelopment = url.protocol === "http:" && (url.hostname === "localhost" || url.hostname === "127.0.0.1");
  if ((url.protocol !== "https:" && !localDevelopment) || !url.pathname.endsWith(`/${CAPTURE_ROUTE_SEGMENT}`)) {
    return { status: "invalid", reason: "route" };
  }
  const rawFragment = url.hash.slice(1);
  if (!rawFragment) return { status: "absent" };
  if (!rawFragment.startsWith(CAPTURE_FRAGMENT_MARKER)) return { status: "invalid", reason: "route" };
  let text: string;
  try {
    text = decodeURIComponent(rawFragment.slice(CAPTURE_FRAGMENT_MARKER.length)).trim();
  } catch {
    return { status: "invalid", reason: "encoding" };
  }
  if (!text) return { status: "invalid", reason: "empty" };
  if (hasUnsafeControlCharacter(text)) return { status: "invalid", reason: "unsafe-text" };
  const byteLength = new TextEncoder().encode(text).byteLength;
  if (byteLength > CAPTURE_INPUT_MAX_BYTES) return { status: "invalid", reason: "too-large" };
  return { status: "ready", kind: parseWebUrl(text) === null ? "text" : "url", text, byteLength };
}

/** Removes capture payloads from the address bar before React renders. */
export function captureUrlWithoutFragment(urlInput: string | URL): string {
  const url = typeof urlInput === "string" ? new URL(urlInput) : new URL(urlInput.href);
  url.hash = "";
  return `${url.pathname}${url.search}`;
}

/**
 * Page-process replay protection. Only a non-reversible fingerprint is kept;
 * the payload is neither persisted nor logged. Clearing the fragment handles
 * refresh/back-button replay. This is accidental-replay protection, not auth.
 */
export class CaptureHandoffReplayGuard {
  private readonly consumed = new Set<string>();

  consume(url: string | URL): CaptureHandoffInput {
    const parsed = parseCaptureHandoff(url);
    if (parsed.status !== "ready") return parsed;
    const fingerprint = fingerprintText(parsed.text);
    if (this.consumed.has(fingerprint)) return { status: "replay" };
    this.consumed.add(fingerprint);
    return parsed;
  }
}

/** The parser never writes; the UI calls this only from its explicit confirm action. */
export async function confirmCaptureHandoff(
  capture: CaptureHandoffInput,
  save: (text: string) => Promise<void>,
): Promise<void> {
  if (capture.status !== "ready") throw new Error("A valid capture preview is required.");
  await save(capture.text);
}

function hasUnsafeControlCharacter(value: string): boolean {
  for (const character of value) {
    const code = character.codePointAt(0)!;
    if ((code < 0x20 && code !== 0x09 && code !== 0x0a && code !== 0x0d) || code === 0x7f) return true;
  }
  return false;
}

function fingerprintText(value: string): string {
  let hash = 0xcbf29ce484222325n;
  for (const byte of new TextEncoder().encode(value)) {
    hash ^= BigInt(byte);
    hash = BigInt.asUintN(64, hash * 0x100000001b3n);
  }
  return `${value.length}:${hash.toString(16).padStart(16, "0")}`;
}
